/**
 * What a run actually consulted, derived from the tool names it called.
 *
 * A finding is only as good as what fed it, and "what did it look at?" is the first question a senior
 * reader asks — but tool names are the wrong answer to it. This maps each tool onto the source a
 * reader would recognise, so the page can name its inputs at a glance.
 *
 * Deliberately not a list of what was *excluded*: the set of sources a run did not consult is
 * unbounded, so enumerating it is arbitrary and reads as hedging.
 *
 * Derived rather than stored, so it stays correct for runs recorded before this existed and cannot
 * drift from `toolsUsed`.
 *
 * The tool names are written out rather than imported: the registry lives in
 * `@tempo-io/backend-mcp-tools`, and pulling a backend package into a DTO shared with the frontend
 * to gain a type link would be a bad trade. `insights-tools.test.ts` in `apps/sir` — which already
 * depends on both — asserts every read-only tool the agent can call is mapped here, so a rename or a
 * new tool fails a test instead of quietly vanishing from the row.
 */
export const runSourceKinds = [
  'workItems',
  'objectives',
  'effort',
  'scheduler',
  'teams',
  'planningFields',
  'signals',
  'feedback',
  'integrations',
] as const;

export type RunSourceKind = (typeof runSourceKinds)[number];

const SOURCE_BY_TOOL: Record<string, RunSourceKind> = {
  list_loops: 'workItems',
  list_item_types: 'workItems',
  list_item_statuses: 'workItems',
  search_items: 'workItems',
  list_objectives: 'objectives',
  list_item_objectives: 'objectives',
  list_key_results: 'objectives',
  list_key_result_items: 'objectives',
  get_objective_health: 'objectives',
  get_objectives_health_summary: 'objectives',
  // A precomputed rollup of the work/objective/effort tools, organised by objective (connectivity and
  // per-objective work), so it reads as an objectives source rather than a new kind of data.
  get_portfolio_digest: 'objectives',
  get_item_health: 'effort',
  get_items_health_summary: 'effort',
  list_estimations: 'effort',
  get_item_schedules: 'scheduler',
  list_scenarios: 'scheduler',
  get_scenario_impact_summary: 'scheduler',
  list_teams: 'teams',
  get_team: 'teams',
  search_teams: 'teams',
  get_team_members: 'teams',
  get_team_availability: 'teams',
  search_workload_schemes: 'teams',
  get_workload_scheme: 'teams',
  list_custom_fields: 'planningFields',
  get_custom_field_values: 'planningFields',
  list_signals: 'signals',
  fim_list_feedback: 'feedback',
  fim_list_custom_fields: 'feedback',
  fim_list_product_areas: 'feedback',
  list_integrations: 'integrations',
};

/**
 * The sources behind a run, in a fixed order so the row does not reshuffle between runs. Unmapped
 * tool names are dropped rather than shown raw — a tool nobody has named yet is exactly the API
 * syntax this is here to replace.
 */
export const runSources = (toolsUsed: string[]): RunSourceKind[] => {
  const present = new Set(toolsUsed.map((tool) => SOURCE_BY_TOOL[tool]).filter(Boolean));
  return runSourceKinds.filter((kind) => present.has(kind));
};
